const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Parses a value for display. A date-only string ("2026-08-01") is a
 * calendar day, so it becomes local midnight of that day; `new Date()` would
 * read it as UTC midnight and a US browser would show the day before. Any
 * other string (a timestamp) parses as usual. Returns null when the value is
 * empty or not a date.
 */
export const parseDisplayDate = (
  value: string | null | undefined
): Date | null => {
  if (!value) return null
  const match = dateOnlyPattern.exec(value)
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const monthYear = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
})

/** How much of a date to show. The day always comes before the month. */
export type DayMonthOptions = Readonly<{
  /** "Oct" (the default) or "October". */
  month?: "short" | "long"
  year?: boolean
  /** "Fri" or "Friday", before the day. */
  weekday?: "short" | "long"
  /** ", 3:45 PM" after the date. */
  time?: boolean
  /** The zone to read the instant in. The reader's own when left out. */
  timeZone?: string
}>

/* Built once per shape: a new formatter costs about seventy format calls. */
const formatters = new Map<string, Intl.DateTimeFormat>()

const formatterFor = (options: DayMonthOptions): Intl.DateTimeFormat => {
  const key = JSON.stringify([
    options.month ?? "short",
    options.year ?? false,
    options.weekday ?? null,
    options.time ?? false,
    options.timeZone ?? null,
  ])
  let formatter = formatters.get(key)
  if (!formatter) {
    // en-US for the words: en-GB writes "Sept". The order is set below.
    formatter = new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: options.month ?? "short",
      ...(options.year ? { year: "numeric" as const } : {}),
      ...(options.weekday ? { weekday: options.weekday } : {}),
      ...(options.time ? { hour: "numeric" as const, minute: "2-digit" as const } : {}),
      ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    })
    formatters.set(key, formatter)
  }
  return formatter
}

/**
 * The house date: the day, then the month. "2 Oct", "2 Oct 2026",
 * "Fri 2 Oct", "2 October 2026", "2 Oct 2026, 3:45 PM". Every app formats
 * a date for people through this; data keeps ISO dates.
 */
export const formatDayMonth = (
  date: Date,
  options: DayMonthOptions = {}
): string => {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {}
  for (const part of formatterFor(options).formatToParts(date))
    parts[part.type] = part.value
  const day = [parts.weekday, parts.day, parts.month, parts.year]
    .filter(Boolean)
    .join(" ")
  return options.time
    ? `${day}, ${parts.hour}:${parts.minute} ${parts.dayPeriod ?? ""}`.trim()
    : day
}

/**
 * A date range for people, from two optional date-only values: a whole
 * month reads "August 2026", anything else "1 Aug to 15 Sep 2026", and an
 * open end reads "from 1 Aug 2026" or "through 31 Aug 2026". Null when
 * neither end is set.
 */
export const formatDateRange = (
  start: string | null | undefined,
  end: string | null | undefined
): string | null => {
  const from = parseDisplayDate(start)
  const to = parseDisplayDate(end)
  if (from && to) {
    const lastOfMonth = new Date(from.getFullYear(), from.getMonth() + 1, 0)
    if (
      from.getDate() === 1 &&
      to.getFullYear() === from.getFullYear() &&
      to.getMonth() === from.getMonth() &&
      to.getDate() === lastOfMonth.getDate()
    ) {
      return monthYear.format(from)
    }
    return `${formatDayMonth(from, { year: from.getFullYear() !== to.getFullYear() })} to ${formatDayMonth(to, { year: true })}`
  }
  if (from) return `from ${formatDayMonth(from, { year: true })}`
  if (to) return `through ${formatDayMonth(to, { year: true })}`
  return null
}

/** One date-only value for people: "26 Sep 2026". */
export const formatDisplayDate = (value: string | null | undefined) => {
  const date = parseDisplayDate(value)
  return date ? formatDayMonth(date, { year: true }) : null
}
